import { useState, useEffect } from 'react';
import './Transformations.css';

const Transformations = ({ setActiveTab }) => {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [sourceVendor, setSourceVendor] = useState('');
  const [targetVendor, setTargetVendor] = useState('');

  const [mappings, setMappings] = useState([]);
  const [sourceFields, setSourceFields] = useState([]);
  const [targetFields, setTargetFields] = useState([]);

  useEffect(() => {
    setActiveTab('transformations');
  }, [setActiveTab]);

  const vendorFields = {
    tracelink: {
      fields: [
        { id: 'serial_number', name: 'Serial Number', type: 'String', required: true },
        { id: 'batch_lot', name: 'Batch/Lot', type: 'String', required: true },
        { id: 'expiry_date', name: 'Expiry Date', type: 'Date', required: false },
        { id: 'product_code', name: 'Product Code', type: 'String', required: true },
        { id: 'gtin', name: 'GTIN', type: 'String', required: true },
        { id: 'quantity', name: 'Quantity', type: 'Number', required: true },
        { id: 'po_number', name: 'PO Number', type: 'String', required: false },
        { id: 'event_time', name: 'Event Time', type: 'DateTime', required: true }
      ]
    },
    sap: {
      fields: [
        { id: 'sn', name: 'SN', type: 'String', required: true },
        { id: 'batch', name: 'Batch', type: 'String', required: true },
        { id: 'exp_date', name: 'Expiration Date', type: 'Date', required: false },
        { id: 'material', name: 'Material', type: 'String', required: true },
        { id: 'ean', name: 'EAN', type: 'String', required: true },
        { id: 'qty', name: 'Quantity', type: 'Integer', required: true },
        { id: 'purchase_order', name: 'Purchase Order', type: 'String', required: false },
        { id: 'timestamp', name: 'Timestamp', type: 'Timestamp', required: true }
      ]
    },
    rfxcel: {
      fields: [
        { id: 'serialNumber', name: 'serialNumber', type: 'String', required: true },
        { id: 'lotNumber', name: 'lotNumber', type: 'String', required: true },
        { id: 'expirationDate', name: 'expirationDate', type: 'Date', required: false },
        { id: 'productId', name: 'productId', type: 'String', required: true },
        { id: 'gtin14', name: 'gtin14', type: 'String', required: true },
        { id: 'itemQuantity', name: 'itemQuantity', type: 'Number', required: true },
        { id: 'orderNumber', name: 'orderNumber', type: 'String', required: false },
        { id: 'eventTimestamp', name: 'eventTimestamp', type: 'DateTime', required: true }
      ]
    },
    optel: {
      fields: [
        { id: 'serial', name: 'Serial', type: 'String', required: true },
        { id: 'lot', name: 'Lot', type: 'String', required: true },
        { id: 'exp', name: 'Exp', type: 'Date', required: false },
        { id: 'product', name: 'Product', type: 'String', required: true },
        { id: 'gtin', name: 'GTIN', type: 'String', required: true },
        { id: 'count', name: 'Count', type: 'Integer', required: true },
        { id: 'order', name: 'Order', type: 'String', required: false },
        { id: 'time', name: 'Time', type: 'DateTime', required: true }
      ]
    },
    verify: {
      fields: [
        { id: 'ser_num', name: 'Serial Number', type: 'String', required: true },
        { id: 'batch_id', name: 'Batch ID', type: 'String', required: true },
        { id: 'exp_dt', name: 'Expiry Date', type: 'Date', required: false },
        { id: 'prod_cd', name: 'Product Code', type: 'String', required: true },
        { id: 'gtin_cd', name: 'GTIN Code', type: 'String', required: true },
        { id: 'qty_val', name: 'Quantity Value', type: 'Number', required: true },
        { id: 'po_ref', name: 'PO Reference', type: 'String', required: false },
        { id: 'evt_ts', name: 'Event Timestamp', type: 'DateTime', required: true }
      ]
    },
    tracelink_serial: {
      fields: [
        { id: 'requestId', name: 'Request ID', type: 'String', required: true },
        { id: 'productCode', name: 'Product Code', type: 'String', required: true },
        { id: 'quantity', name: 'Quantity', type: 'Integer', required: true },
        { id: 'serialType', name: 'Serial Type', type: 'String', required: true },
        { id: 'siteId', name: 'Site ID', type: 'String', required: true }
      ]
    },
    rfxcel_serial: {
      fields: [
        { id: 'jobId', name: 'Job ID', type: 'String', required: true },
        { id: 'gtin', name: 'GTIN', type: 'String', required: true },
        { id: 'serialCount', name: 'Serial Count', type: 'Integer', required: true },
        { id: 'lotNumber', name: 'Lot Number', type: 'String', required: false },
        { id: 'expirationDate', name: 'Expiration Date', type: 'Date', required: false }
      ]
    }
  };

  const loadSourceFields = (vendor) => {
    setSourceVendor(vendor);
    if (vendor && vendorFields[vendor]) {
      setSourceFields(vendorFields[vendor].fields);

    } else {
      setSourceFields([]);
    }
    setMappings([]);
  };

  const loadTargetFields = (vendor) => {
    setTargetVendor(vendor);
    if (vendor && vendorFields[vendor]) {
      setTargetFields(vendorFields[vendor].fields);

    } else {
      setTargetFields([]);
    }
    setMappings([]);
  };

  const handleMapping = (sourceFieldId, targetFieldId) => {
    const newMappings = [...mappings];
    const existingIndex = newMappings.findIndex(m => m.source === sourceFieldId);

    if (existingIndex >= 0) {
      if (targetFieldId) {
        newMappings[existingIndex].target = targetFieldId;
      } else {
        newMappings.splice(existingIndex, 1);
      }
    } else if (targetFieldId) {
      newMappings.push({ source: sourceFieldId, target: targetFieldId });
    }

    setMappings(newMappings);
  };

  const getMappedTarget = (sourceFieldId) => {
    const mapping = mappings.find(m => m.source === sourceFieldId);
    return mapping ? mapping.target : '';
  };



  const saveTransformation = () => {
    // Simulate save
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 3000);
  };

  const clearTransformation = () => {
    setSourceVendor('');
    setTargetVendor('');
    setSourceFields([]);
    setTargetFields([]);
    setMappings([]);

  };

  const loadTemplate = () => {
    // Load a predefined template
    setSourceVendor('tracelink');
    setTargetVendor('sap');
    loadSourceFields('tracelink');
    loadTargetFields('sap');
    setMappings([
      { source: 'serial_number', target: 'sn' },
      { source: 'batch_lot', target: 'batch' },
      { source: 'expiry_date', target: 'exp_date' },
      { source: 'product_code', target: 'material' },
      { source: 'gtin', target: 'ean' },
      { source: 'quantity', target: 'qty' },
      { source: 'po_number', target: 'purchase_order' },
      { source: 'event_time', target: 'timestamp' }
    ]);
  };

  return (
    <>
      {/* Top Header */}
      <header className="top-header">
        <div className="breadcrumb">
          <span>Relay</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Transformations</span>
        </div>
      </header>



      {/* Transformations Content */}
      <div className="tab-content active">
        {/* Success Alert */}
        {showSuccessAlert && (
          <div className="alert alert-success">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Transformation saved successfully!</span>
          </div>
        )}

        {/* Transformation Builder */}
        <div className="transformation-container">
          <div className="transform-header">
            <h2 className="transform-title">Serial Number Field Transformation</h2>
            <div className="transform-actions">
              <button className="header-btn" onClick={loadTemplate}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Load Template
              </button>
              <button className="header-btn secondary" onClick={clearTransformation}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
                Clear
              </button>
            </div>
          </div>

          {/* Vendor Selection */}
          <div className="vendor-selection">
            <div className="vendor-card">
              <div className="vendor-label">Source Specification</div>
              <select
                className="vendor-select"
                value={sourceVendor}
                onChange={(e) => loadSourceFields(e.target.value)}
              >
                <option value="">Select vendor...</option>
                <option value="tracelink">TraceLink</option>
                <option value="tracelink_serial">TraceLink Serial Number Generation</option>
                <option value="sap">SAP ATTP</option>
                <option value="rfxcel">rfXcel</option>
                <option value="optel">Optel</option>
                <option value="verify">Verify Brand</option>
                <option value="custom">Custom Format</option>
              </select>
            </div>

            <div className="arrow-container">
              <div className="transform-arrow">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </div>
            </div>

            <div className="vendor-card">
              <div className="vendor-label">Target Specification</div>
              <select
                className="vendor-select"
                value={targetVendor}
                onChange={(e) => loadTargetFields(e.target.value)}
              >
                <option value="">Select vendor...</option>
                <option value="tracelink">TraceLink</option>
                <option value="sap">SAP ATTP</option>
                <option value="rfxcel">rfXcel</option>
                <option value="rfxcel_serial">rfXcel Serial Number Generation</option>
                <option value="optel">Optel</option>
                <option value="verify">Verify Brand</option>
                <option value="custom">Custom Format</option>
              </select>
            </div>
          </div>


          {/* Field Mapping Section */}
          {sourceFields.length > 0 && targetFields.length > 0 && (
            <div className="mapping-section">
              <div className="mapping-header">
                <h3>Field Mapping Configuration</h3>
                <div className="mapping-actions">
                  <button className="mapping-btn">Auto-Map</button>
                  <button className="mapping-btn">Import CSV</button>
                  <button className="mapping-btn">Export Template</button>
                </div>
              </div>

              <div className="mapping-grid">
                <div className="mapping-column source">
                  <div className="column-header">
                    <span className="column-title">Source Fields ({sourceVendor})</span>
                    <span className="column-count">{sourceFields.length} fields</span>
                  </div>
                  <div className="fields-list">
                    {sourceFields.map(field => (
                      <div key={field.id} className={`field-item ${field.required ? 'required' : ''}`}>
                        <div className="field-info">
                          <span className="field-name">{field.name}</span>
                          <span className="field-type">{field.type}</span>
                          {field.required && <span className="field-required">*</span>}
                        </div>
                        <select
                          className="field-mapping-select"
                          value={getMappedTarget(field.id)}
                          onChange={(e) => handleMapping(field.id, e.target.value)}
                        >
                          <option value="">-- Not Mapped --</option>
                          {targetFields.map(targetField => (
                            <option key={targetField.id} value={targetField.id}>
                              {targetField.name} ({targetField.type})
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mapping-visualizer">
                  <svg className="connection-lines" width="100" height="400">
                    {mappings.map((mapping, index) => {
                      const sourceIndex = sourceFields.findIndex(f => f.id === mapping.source);
                      const targetIndex = targetFields.findIndex(f => f.id === mapping.target);
                      if (sourceIndex >= 0 && targetIndex >= 0) {
                        const y1 = 30 + sourceIndex * 60;
                        const y2 = 30 + targetIndex * 60;
                        return (
                          <line
                            key={index}
                            x1="0"
                            y1={y1}
                            x2="100"
                            y2={y2}
                            stroke="#6366f1"
                            strokeWidth="2"
                            strokeDasharray="4"
                            opacity="0.6"
                          />
                        );
                      }
                      return null;
                    })}
                  </svg>
                </div>

                <div className="mapping-column target">
                  <div className="column-header">
                    <span className="column-title">Target Fields ({targetVendor})</span>
                    <span className="column-count">{targetFields.length} fields</span>
                  </div>
                  <div className="fields-list">
                    {targetFields.map(field => {
                      const isMapped = mappings.some(m => m.target === field.id);
                      return (
                        <div key={field.id} className={`field-item ${field.required ? 'required' : ''} ${isMapped ? 'mapped' : ''}`}>
                          <div className="field-info">
                            <span className="field-name">{field.name}</span>
                            <span className="field-type">{field.type}</span>
                            {field.required && <span className="field-required">*</span>}
                          </div>
                          {isMapped && (
                            <span className="field-status">✓ Mapped</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Transformation Rules */}
              <div className="transformation-rules">
                <h4>Transformation Rules</h4>
                <div className="rules-grid">
                  <div className="rule-card">
                    <div className="rule-header">
                      <span className="rule-title">Date Format Conversion</span>
                      <label className="switch">
                        <input type="checkbox" defaultChecked />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="rule-description">
                      Automatically convert date formats between vendors (YYYY-MM-DD ↔ MM/DD/YYYY)
                    </div>
                  </div>

                  <div className="rule-card">
                    <div className="rule-header">
                      <span className="rule-title">Null Value Handling</span>
                      <label className="switch">
                        <input type="checkbox" defaultChecked />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="rule-description">
                      Replace null values with default values or skip fields
                    </div>
                  </div>

                  <div className="rule-card">
                    <div className="rule-header">
                      <span className="rule-title">Data Validation</span>
                      <label className="switch">
                        <input type="checkbox" defaultChecked />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="rule-description">
                      Validate data types and formats before transformation
                    </div>
                  </div>

                  <div className="rule-card">
                    <div className="rule-header">
                      <span className="rule-title">Error Logging</span>
                      <label className="switch">
                        <input type="checkbox" defaultChecked />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="rule-description">
                      Log all transformation errors for debugging
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="transformation-footer">
                <div className="footer-info">
                  <span className="mapping-summary">
                    {mappings.length} of {sourceFields.filter(f => f.required).length} required fields mapped
                  </span>
                </div>
                <div className="footer-actions">
                  <button className="btn-secondary">Save as Draft</button>
                  <button className="btn-primary" onClick={saveTransformation}>
                    Save Transformation
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default Transformations;